// Third Party
import React, { Component } from 'react';
import { SortingRule } from 'react-table';

// Actions
import { startLoaderAction, stopLoaderAction } from '@/redux/modules/ui/loaders';

// Components
import DelegateBanner from '../auth/DelegateBanner';
import { AnnouncementBanner } from '../announcements';
import StartComparison from './NewComparison/StartComparison';
import SecondaryHeader from '@/views/components/layout/SecondaryHeader';
import { LoadModelsModal, ContactPortfoliosModal, EditPortfolioModal } from '../../components/modals';
import LinkHeader from '@/views/components/link/LinkHeader';
import LinkLogo from '@/views/components/link/LinkLogo';
import LinkStartComparison from './NewComparison/LinkStartComparison';

// Enums
import { HoldingAllocationMode } from '@/enums/Portfolio';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Legacy Component
import LegacyEmptyComparison from './LegacyEmptyComparison';

// Types
import { SortingField } from '../../../types/data';
import User from '@/types/user';
import GenericContact from '@/types/generic-contact';
import Portfolio from '@/types/portfolio';
import ComparisonItem from '@/types/comparison-item';
import Locale from '@/types/locale';
import AppMode from '@/types/app-mode';
import { ReduxState } from '@/redux/modules/types';

// Utils
import heapUtils, { HEAP_CUSTOM_EVENTS } from '../../../utils/3rd-party/heap';
import { formatMessage } from '../../../utils/i18n';
import { getContactLayout } from '../../../utils/contacts';
import disclaimers from '../../../utils/disclaimers';
import { clonePortfolioAsProxy } from '../../../utils/portfolio/utils';
import { userHasPermissionEnabled, PERMISSIONS } from '../../../utils/permissions';
import { togglePortfolioEditor } from '../../../redux/modules/common/actionCreators';
import { connect } from 'react-redux';
import { ADD_PORTFOLIOS_TO_COMPARISON } from '@/redux/modules/comparison';
import { withLoaderAction } from '@/utils/promise';
import { shouldClosePortfolioEditor } from '@/redux/modules/ai-statement-reader-multi-portfolio';
import { canAccessMultiPortfoliosStatementReader } from '@/utils/feature-access';

type DispatchProps = {
  togglePortfolioEditor: (show: boolean) => void;
  startLoaderAction: (type: string) => void;
  stopLoaderAction: (type: string) => void;
};

type StateProps = {
  editPortfolioModalIsVisible: boolean;
  shouldCloseEditor: boolean;
};

type Props = DispatchProps &
  StateProps & {
    queries: {
      contactPortfolios: string;
    };
    user: User;
    selectedContact?: {
      contact: GenericContact;
      portfolios?: Portfolio[];
      hasMore: {
        portfolios: boolean;
      };
    };
    isComparingPortfolios: boolean;
    locale: Locale;
    onCloseComparison: () => void;
    addPortfoliosToComparison: (portfolios: Portfolio[]) => Promise<void>;
    onSearchContactPortfolios: (query: string) => void;
    resetRoute: () => void;
    loadContactPortfolios: (contact: GenericContact) => Promise<void>;
    onLoadMoreContactPortfolios: (sort?: SortingField[]) => void;
    resetCurrentContact: () => void;
    savePortfolio: (portfolio: Portfolio, contact?: GenericContact) => Promise<Portfolio>;
    resetPortfolio: () => void;
    loadNewPortfolio: () => void;
    loadPortfolioForEdit: (portfolio: Portfolio, comparisonItem?: ComparisonItem, isPinned?: boolean) => Promise<void>;
    getComparisonContact: () => Promise<GenericContact | undefined>;
    setPortfolioContact: (contact: GenericContact) => void;
  };

type State = {
  showModelPortfoliosModal: boolean;
  showContactPortfoliosModal: boolean;
  showPortfolioUploadModal: boolean;
  pendingPortfolios: Portfolio[];
  savedMultiPortfolios: Portfolio[];
  sorting: SortingRule[];
};

class EmptyComparisonV2 extends Component<Props, State> {
  readonly state: State = {
    showModelPortfoliosModal: false,
    showContactPortfoliosModal: false,
    showPortfolioUploadModal: false,
    pendingPortfolios: [],
    savedMultiPortfolios: [],
    sorting: [],
  };

  componentDidMount() {
    this.props.resetRoute();
  }

  addPortfoliosToComparison = async (portfolios: Portfolio[]) => {
    const { startLoaderAction, stopLoaderAction } = this.props;
    await withLoaderAction(ADD_PORTFOLIOS_TO_COMPARISON.type, startLoaderAction, stopLoaderAction, async () => {
      if (portfolios.some(p => disclaimers.portfolioHasMissingOrInvalidHoldings(p))) {
        this.setState({ pendingPortfolios: portfolios }, () => {
          this.hideVisibleModal();
          this.handlePendingPortfolios();
        });

        return Promise.resolve();
      }

      return this.props.addPortfoliosToComparison(portfolios);
    });
  };

  handlePendingPortfolios = () => {
    const { user } = this.props;
    const { pendingPortfolios } = this.state;
    if (pendingPortfolios && pendingPortfolios.length) {
      const portfolioWithMissingHolding = pendingPortfolios.find(p => disclaimers.portfolioHasMissingOrInvalidHoldings(p));
      if (portfolioWithMissingHolding) {
        pendingPortfolios.splice(pendingPortfolios.indexOf(portfolioWithMissingHolding), 1);
        const portfolioClone = clonePortfolioAsProxy(portfolioWithMissingHolding);

        if (user && userHasPermissionEnabled(user, PERMISSIONS.PORTFOLIO_UNITS_MODE)) {
          portfolioClone.allocationMode = HoldingAllocationMode.Units;
        }

        this.showEditPortfolioModal(portfolioClone);
      } else {
        this.setState({ pendingPortfolios: [] });
        this.addPortfoliosToComparison(pendingPortfolios);
      }
    }
    this.props.stopLoaderAction(ADD_PORTFOLIOS_TO_COMPARISON.type);
  };

  hideVisibleModal = () => {
    const { showModelPortfoliosModal, showContactPortfoliosModal } = this.state;
    if (showModelPortfoliosModal === true) {
      this.hideModelPortfoliosModal();
    } else if (showContactPortfoliosModal === true) {
      this.hideContactPortfoliosModal();
    }
  };

  hideModelPortfoliosModal = () => this.setState({ showModelPortfoliosModal: false });

  showModelPortfoliosModal = () => this.setState({ showModelPortfoliosModal: true });

  hideContactPortfoliosModal = () => this.setState({ showContactPortfoliosModal: false });

  showContactPortfoliosModal = () => this.setState({ showContactPortfoliosModal: true });

  hideEditPortfolioModal = () => {
    this.props.resetPortfolio();
    this.props.togglePortfolioEditor(false);
  };

  showEditPortfolioModal = async (portfolio?: Portfolio) => {
    const contact = await this.props.getComparisonContact();

    this.props.resetPortfolio();

    if (portfolio?._id === undefined) {
      this.props.loadNewPortfolio();
    } else {
      this.props.loadPortfolioForEdit(portfolio);
    }

    if (contact) {
      this.props.setPortfolioContact(contact);
    }

    this.props.togglePortfolioEditor(true);
  };

  showPortfolioUploadModal = () => this.setState({ showPortfolioUploadModal: true });

  hidePortfolioUploadModal = () => this.setState({ showPortfolioUploadModal: false });

  savePortfolio = (newPortfolio: Portfolio): Promise<Portfolio> => {
    const { user, shouldCloseEditor, addPortfoliosToComparison } = this.props;
    const id = newPortfolio._id;
    const isMultiPortfolioEnabled = canAccessMultiPortfoliosStatementReader(user);

    return this.props.savePortfolio(newPortfolio).then(portfolio => {
      heapUtils.track(HEAP_CUSTOM_EVENTS.NEW_COMPARISON);
      const { pendingPortfolios } = this.state;

      if (pendingPortfolios.length) {
        this.setState({ pendingPortfolios: pendingPortfolios.filter(p => p._id !== id).concat([portfolio]) }, () => {
          if (shouldCloseEditor) {
            this.hideEditPortfolioModal();
          }
        });
      } else {
        // Multi-portfolio feature: collect saved portfolios and add them all at the end
        // This prevents EmptyComparison from unmounting mid-carousel
        if (isMultiPortfolioEnabled && !shouldCloseEditor) {
          // Carousel is still active, collect the portfolio
          this.setState({ savedMultiPortfolios: [...this.state.savedMultiPortfolios, portfolio] });
          return portfolio;
        }

        // Add to comparison (either feature disabled, not in multi-portfolio mode, or carousel complete)
        const portfoliosToAdd =
          isMultiPortfolioEnabled && this.state.savedMultiPortfolios.length > 0 ? [...this.state.savedMultiPortfolios, portfolio] : [portfolio];

        addPortfoliosToComparison(portfoliosToAdd).then(() => {
          if (isMultiPortfolioEnabled) {
            this.setState({ savedMultiPortfolios: [] }); // Clear the collected portfolios
          }
          if (shouldCloseEditor) {
            this.hideEditPortfolioModal();
          }
        });
      }
      return portfolio;
    });
  };

  cancelPortfolioEdit = () => {
    // Multi-portfolio feature: if we have collected portfolios during the carousel,
    // add them to comparison before closing
    const { user, addPortfoliosToComparison } = this.props;
    const { savedMultiPortfolios } = this.state;
    const isMultiPortfolioEnabled = canAccessMultiPortfoliosStatementReader(user);

    if (isMultiPortfolioEnabled && savedMultiPortfolios.length > 0) {
      addPortfoliosToComparison(savedMultiPortfolios).then(() => {
        this.setState({ savedMultiPortfolios: [] });
        this.hideEditPortfolioModal();
      });
    } else {
      this.hideEditPortfolioModal();
    }
  };

  // Render functions
  renderActionModals = () => {
    const { queries, user, selectedContact, isComparingPortfolios, editPortfolioModalIsVisible } = this.props;
    const { showModelPortfoliosModal, showContactPortfoliosModal } = this.state;
    return (
      <>
        <LoadModelsModal
          user={user}
          show={showModelPortfoliosModal}
          onCancel={this.hideModelPortfoliosModal}
          addPortfoliosToComparison={this.addPortfoliosToComparison}
        />

        <ContactPortfoliosModal
          isComparing={isComparingPortfolios}
          query={queries.contactPortfolios}
          show={showContactPortfoliosModal}
          selectedContact={selectedContact}
          onCancel={this.hideContactPortfoliosModal}
          onSearchContactPortfolios={this.props.onSearchContactPortfolios}
          loadContactPortfolios={this.props.loadContactPortfolios}
          loadMoreContactPortfolios={this.props.onLoadMoreContactPortfolios}
          addPortfoliosToComparison={this.addPortfoliosToComparison}
          resetCurrentContact={this.props.resetCurrentContact}
          layout={getContactLayout(user)}
        />

        <EditPortfolioModal
          show={editPortfolioModalIsVisible}
          onSave={this.savePortfolio}
          onCancel={this.cancelPortfolioEdit}
          saveLabel={formatMessage('Comparison.AddToComparison')}
          saveNotification={formatMessage('HoldingsEditor.Created.PORTFOLIO')}
          onExited={this.handlePendingPortfolios}
        />
      </>
    );
  };

  renderLinkMode = () => {
    const { user } = this.props;
    return (
      <>
        <LinkHeader user={user} middleItems={<LinkLogo user={user} />} />
        <LinkStartComparison user={user} showEditPortfolioModal={this.showEditPortfolioModal} />
      </>
    );
  };

  renderDefaultMode = () => {
    const { user } = this.props;
    return (
      <>
        <SecondaryHeader onBack={this.props.onCloseComparison} />
        <StartComparison
          user={user}
          showContactPortfoliosModal={this.showContactPortfoliosModal}
          showModelPortfoliosModal={this.showModelPortfoliosModal}
          showEditPortfolioModal={this.showEditPortfolioModal}
        />
      </>
    );
  };

  render() {
    const { user } = this.props;
    const isLinkMode = user.appConfig.mode === AppMode.LINK;

    return (
      <>
        <div className="start-comparison-screen">
          <DelegateBanner />
          <AnnouncementBanner />
          {isLinkMode ? this.renderLinkMode() : this.renderDefaultMode()}
        </div>
        {this.renderActionModals()}
      </>
    );
  }
}

const EmptyComparison = (props: Props) => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <EmptyComparisonV2 {...props} />;
  }
  return <LegacyEmptyComparison {...props} />;
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  editPortfolioModalIsVisible: state.comparison.portfolioEditor.isVisible,
  shouldCloseEditor: shouldClosePortfolioEditor(state.aiStatementReaderMultiPortfolio),
});

export default connect(mapStateToProps, {
  togglePortfolioEditor,
  startLoaderAction,
  stopLoaderAction,
})(EmptyComparison);
